/**
 * Shared Framer Motion variants + easings — one rhythm across the whole app.
 * Keep durations 200–340ms; respect prefers-reduced-motion at the component
 * level (Framer reads it automatically when you use `MotionConfig reducedMotion`
 * or the `useReducedMotion` hook; our entrances are subtle fades so they degrade
 * gracefully).
 *
 * MOTION SPLIT (SYS-9): these Framer variants own component ENTER/EXIT. The CSS
 * keyframes in styles/global.css own only infinite/AMBIENT effects (pulse-ring,
 * flow-edge, float, status-dot pulse). The old CSS `.rise*` entrance stagger was
 * a duplicate of `staggerContainer`/`cardItem` below and has been removed — use
 * these variants for entrances, not CSS classes.
 */
import type { Variants, Transition } from 'framer-motion';

export const EASE_OUT = [0.22, 1, 0.36, 1] as const;
export const SPRING: Transition = { type: 'spring', stiffness: 380, damping: 30 };

/** Page-level entrance (use on the PageContainer). */
export const pageMotion = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.32, ease: EASE_OUT },
} as const;

/** Stagger container — children use `cardItem`. */
export const staggerContainer: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.055, delayChildren: 0.04 } },
};

/** A card/section item inside a stagger container. */
export const cardItem: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.34, ease: EASE_OUT } },
};

/** A subtle list-row item. */
export const rowItem: Variants = {
  hidden: { opacity: 0, y: 6 },
  show: { opacity: 1, y: 0, transition: { duration: 0.26, ease: EASE_OUT } },
};

/** Tappable hover/press feel for cards + buttons. */
export const tapCard = {
  whileHover: { y: -2 },
  whileTap: { scale: 0.99 },
  transition: SPRING,
} as const;
